import TimeUnits from "../../constants/TimeUnits";
import { setDecelerationInterval } from "../../utils/Timer";
import { parseSearchParamsByUrl } from "../../utils/Url";
import AnalyticsVersions from "./AnalyticsVersions";
/**
 * resolveAnalyticsVersion
 * Helper to detect analytics version by what's currently
 * being loaded.
 */
export var resolveAnalyticsVersion = function () {
    var globalKeyUniversal = getGlobalKeyUniversal();
    var hasUniversal = globalKeyUniversal in window;
    var allExistingDataLayerGlobals = resolveAndGetAllDataLayerNames({ strict: true });
    if ("gtag" in window) {
        if (process.env.IS_GA4_ACTIVE === "true") {
            return AnalyticsVersions.Gtag;
        }
        return hasUniversal ? AnalyticsVersions.GtagUniversal : AnalyticsVersions.Gtag;
    }
    else if (!!allExistingDataLayerGlobals.length) {
        return AnalyticsVersions.GoogleTagManager;
    }
    else if (hasUniversal) {
        return AnalyticsVersions.Universal;
    }
    else if ("_gaq" in window) {
        return AnalyticsVersions.Classic;
    }
    return null;
};
/**
 * resolveAllAnalyticsVersions
 * Helper function to detect all active analytics versions on a page.
 * @warning - this is not always reliable since we cannot distinct GTAG from GTM (Google Tag Manager).
 * When strict mode is disabled it will include GTM anyway in case the dataLayer is found.
 * @param strict
 */
export var resolveAllAnalyticsVersions = function (strict) {
    if (strict === void 0) { strict = false; }
    var activeVersions = [];
    var globalKeyUniversal = getGlobalKeyUniversal();
    var hasUniversal = globalKeyUniversal in window;
    if ("gtag" in window) {
        activeVersions.push(AnalyticsVersions.Gtag);
    }
    if ("dataLayer" in window) {
        if (!("gtag" in window) || !strict) {
            activeVersions.push(AnalyticsVersions.GoogleTagManager);
        }
    }
    if (hasUniversal) {
        activeVersions.push(AnalyticsVersions.Universal);
    }
    if ("_gaq" in window) {
        activeVersions.push(AnalyticsVersions.Classic);
    }
    return activeVersions;
};
/**
 * resolveAnalyticsVersionByTakingIntoAccountThePreferredVersion
 * Helper function to determine whether a preferred analytics version is also active.
 * If so, use that one. Otherwise use the primary one.
 *
 * This is usefull because multiple versions could be active at the same time on a specific website.
 * In case of GA4 additional setup is required for either Google Analytics or Google Tag Manager.
 *
 * @param preferredGaVersion
 */
export var resolveAnalyticsVersionByTakingIntoAccountThePreferredVersion = function (preferredGaVersion, version) {
    if (version === void 0) { version = resolveAnalyticsVersion(); }
    if (version !== preferredGaVersion) {
        var allAnalyticsVersions = resolveAllAnalyticsVersions();
        var isPreferredAlsoActive = allAnalyticsVersions.includes(preferredGaVersion);
        if (isPreferredAlsoActive) {
            return preferredGaVersion;
        }
    }
    return version;
};
/**
 * resolveAnalyticsGlobalByVersion
 * Helper to obtain the Analytics global for the given version
 */
export var resolveAnalyticsGlobalByVersion = function (version) {
    var win = window;
    switch (version) {
        case AnalyticsVersions.Gtag:
            return typeof (win === null || win === void 0 ? void 0 : win["gtag"]) === "function" ? win["gtag"] : function gtag() {
                var args = arguments;
                resolveAndGetAllDataLayerNames().forEach(function (dataLayer) {
                    window[dataLayer] = window[dataLayer] || [];
                    window[dataLayer].push(args);
                });
            };
        case AnalyticsVersions.GoogleTagManager:
            return function () {
                var args = arguments;
                resolveAndGetAllDataLayerNames().forEach(function (dataLayer) {
                    window[dataLayer] = window[dataLayer] || [];
                    window[dataLayer].push(args.length > 1 ? args : args === null || args === void 0 ? void 0 : args[0]);
                });
            };
        case AnalyticsVersions.GtagUniversal:
        case AnalyticsVersions.Universal:
            return win[getGlobalKeyUniversal()] || null;
        case AnalyticsVersions.Classic:
            return win._gaq || null;
    }
};
/**
 * getGlobalKeyUniversal
 * Helper to obtain the key of Universal Analytics
 * in the global scope.
 * Normally this is "ga" but it can be customized.
 */
export var getGlobalKeyUniversal = function () { return window.GoogleAnalyticsObject || "ga"; };
/**
 * resolveAnalyticsVersionWhenReady
 * Returns a promise which resolves the analytics versions.
 * The promise will resolve once analytics has been loaded.
 * @Warning - when multiple versions are loaded, it will take the first detected once...
 */
export var resolveAnalyticsVersionWhenReady = function () {
    return getVersionResolverPromise();
};
/**
 * Private
 */
/**
 * getVersionResolverPromise
 * The following process checks for 30 seconds to determine
 * the loaded Analytics version.
 * @Note - the Promise instance is a singleton
 * @TODO - implement
 */
var getVersionResolverPromise = function () {
    var fn = getVersionResolverPromise;
    if (!fn._promise) {
        fn._promise = new Promise(function (resolve, reject) {
            var startTime = new Date().getTime();
            var timeout = TimeUnits.Second * 30; // 30 seconds
            var stopInterval = setDecelerationInterval(function () {
                try {
                    var version = resolveAnalyticsVersion();
                    var hasExpired = new Date().getTime() - startTime > timeout;
                    if (version || hasExpired) {
                        var isLoaded = true;
                        var shouldHaveUniversal = [AnalyticsVersions.Universal, AnalyticsVersions.GtagUniversal].indexOf(version) !== -1;
                        // check whether universal analytics has been loaded.
                        // @NOTE - gtag makes use of universal...
                        if (shouldHaveUniversal) {
                            var universalGlob = resolveAnalyticsGlobalByVersion(AnalyticsVersions.Universal) || {};
                            isLoaded = isLoaded && universalGlob && universalGlob.loaded === true;
                        }
                        if (isLoaded) {
                            stopInterval();
                            resolve(version);
                        }
                    }
                }
                catch (error) {
                    stopInterval();
                    reject(error);
                }
            }, 100, 1);
        });
    }
    return fn._promise;
};
/**
 * resolveAndGetAllDataLayerNames
 * @param boolean strict, true to check existance in global scope before it is returned in the list, false to only return the found names
 */
export var resolveAndGetAllDataLayerNames = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.strict, strict = _c === void 0 ? false : _c;
    var gtagScriptElements = Array.from(document.querySelectorAll("script[src^='https://www.googletagmanager.com/gtag/js']"));
    var dataLayerNames = gtagScriptElements.map(function (gtagScriptElement) {
        var searchParams = parseSearchParamsByUrl(gtagScriptElement.src);
        if ("l" in searchParams) {
            return searchParams["l"];
        }
    }).filter(function (dataLayerName) {
        var isValidName = dataLayerName !== undefined && dataLayerName;
        var existsInGlobalScope = dataLayerName in window;
        return isValidName && (!strict || existsInGlobalScope);
    });
    var uniqueDataLayerNames = Array.from(new Set(dataLayerNames));
    if (!uniqueDataLayerNames.includes("dataLayer") && !!window.dataLayer) {
        uniqueDataLayerNames.push("dataLayer");
    }
    return uniqueDataLayerNames;
};
//# sourceMappingURL=Utils.js.map